import { Component, HostBinding, Input, OnDestroy, Optional } from '@angular/core';
import { IllustrationSource } from '../illustration/illustration.component';
import { SlideshowHost } from './slideshow-host';

// One image in an app-slideshow.  It renders the same <picture> as
// app-illustration, but takes its geometry - and whether it is the visible
// slide - from the slideshow it is projected into.  Every slide stays in the
// DOM at all times, so search engines see every src/alt pair; only opacity
// tells them apart.
@Component({
  selector: 'app-slide',
  templateUrl: './slide.component.html',
  styleUrls: ['./slide.component.scss']
})
export class SlideComponent implements OnDestroy {

  @Input()
  src?:string;

  @Input()
  webpSrc?:string;

  // Unlike app-illustration a slide is never decorative: this is what a crawler
  // and a screen reader read, so every slide should carry real text.
  @Input()
  alt:string = '';

  // Optional per-breakpoint alternatives, same contract as app-illustration:
  // the first entry whose media query matches wins, so list the narrowest
  // breakpoint first, and every entry must share the ratio of src.
  @Input()
  sources?:IllustrationSource[];

  // Optional so a stray app-slide outside a slideshow still renders its image
  // rather than throwing.  On its own it is simply always the active slide.
  constructor(@Optional() private _slideshow:SlideshowHost) {
    if(this._slideshow) {
      this._slideshow.register(this);
    }
  }

  ngOnDestroy() {
    if(this._slideshow) {
      this._slideshow.unregister(this);
    }
  }

  @HostBinding('class.slide')
  slide = true;

  @HostBinding('class.active')
  get active():boolean {
    return !this._slideshow || this._slideshow.isActive(this);
  }

  @HostBinding('class.cover')
  get cover():boolean {
    return !!this._slideshow && this._slideshow.cover;
  }

  // The slideshow already announces itself and its state, so assistive tech
  // should not read out every slide's alt at once.  Image indexing keys off
  // src and alt rather than ARIA, so this costs nothing on the SEO side.
  @HostBinding('attr.aria-hidden')
  get hidden():string|null {
    return this.active ? null : 'true';
  }

  get backgroundPosition():string {
    return this._slideshow ? this._slideshow.backgroundPosition : 'center';
  }

  get width():number|null {
    return this._slideshow && this._slideshow.hasIntrinsicRatio ? this._slideshow.width : null;
  }

  get height():number|null {
    return this._slideshow && this._slideshow.hasIntrinsicRatio ? this._slideshow.height : null;
  }

  // The first slide is the one that actually paints, so it must not be lazy.
  get eager():boolean {
    return !this._slideshow || this._slideshow.isFirst(this);
  }

}
